import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createVendorType,
  getAllActiveVendorTypes,
  getAllVendorTypes,
  getVendorTypeByID,
  updateVendorType,
} from "./services/vendorType.services";
import { clearVendorTypeDetails } from "./vendorTypeSlice";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { STATUSARRAY } from "../../_contstants/common";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import IconButton from "../../components/common/IconButton";

const CreateVendorType: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const VendorTypeDetail = useAppSelector(
    (state) => state.vendorType.VendorTypeDetail
  );
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);
  const VendorTypeID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.vendorType.formLoading);

  useEffect(() => {
    return () => {
      dispatch(clearVendorTypeDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (VendorTypeID) {
      dispatch(getVendorTypeByID(VendorTypeID));
    }
  }, [VendorTypeID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!VendorTypeID) {
        const insertPayload = {
          VendorType: values.VendorType,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createVendorType(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearVendorTypeDetails());
          if (isIndirect) {
            dispatch(getAllActiveVendorTypes());
          } else {
            dispatch(getAllVendorTypes());
          }
        }
      } else {
        const editPayload = {
          ID: VendorTypeID,
          VendorType: values.VendorType,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateVendorType(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearVendorTypeDetails());
          dispatch(getAllVendorTypes());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={VendorTypeDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem className="mui-drawer-header">
              <Typography.h4 className="m-0">
                {VendorTypeID ? "Update Vendor Type" : "Add Vendor Type"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode="flat"
                size="small"
                title="Close"
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
                icon={RxCross1}
              />
            </GridLayoutItem>

            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="VendorType"
                label="Vendor Type"
                placeholder="Vendor Type"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="IsActive"
                label="Status"
                astrike={true}
                validator={requiredValidator}
                placeholder="i.e. Active"
                component={FormSelectionField}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status?.value,
                    label: status?.label,
                  };
                })}
              />
            </GridLayoutItem>

            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={VendorTypeID ? "Update" : "Save"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />

              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateVendorType;
